import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Slider } from "./ui/slider";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { logMood, logSleep, logCycle, logFitness, logSkin, logNutrition } from "../lib/api";
import { toast } from "sonner";

const moodOptions = [
  { value: "great", label: "Great", emoji: "\u2728" },
  { value: "good", label: "Good", emoji: "\u263A\uFE0F" },
  { value: "okay", label: "Okay", emoji: "\u00b7" },
  { value: "low", label: "Low", emoji: "\u2025" },
  { value: "awful", label: "Awful", emoji: "\u2022" },
];

export default function LogSheets({ open, onClose, onLogged }) {
  const [submitting, setSubmitting] = useState(false);

  // mood
  const [mood, setMood] = useState("good");
  const [energy, setEnergy] = useState([3]);
  const [moodNote, setMoodNote] = useState("");

  // sleep
  const [sleepHours, setSleepHours] = useState("7.5");
  const [sleepQuality, setSleepQuality] = useState([4]);
  const [sleepNote, setSleepNote] = useState("");

  // period
  const [periodStart, setPeriodStart] = useState(new Date());
  const [periodFlow, setPeriodFlow] = useState("medium");
  const [periodNote, setPeriodNote] = useState("");
  const [periodSymptoms, setPeriodSymptoms] = useState([]);

  const PERIOD_SYMPTOMS = [
    "Cramps", "Bloating", "Headache", "Backache",
    "Fatigue", "Tender breasts", "Acne", "Nausea",
    "Mood swings", "Anxiety", "Low appetite", "Cravings",
  ];

  // fitness
  const [activity, setActivity] = useState("walk");
  const [duration, setDuration] = useState("20");
  const [intensity, setIntensity] = useState("low");

  // skin
  const [skinCondition, setSkinCondition] = useState("clear");
  const [skinHydration, setSkinHydration] = useState([4]);

  // nutrition
  const [mealType, setMealType] = useState("lunch");
  const [mealDesc, setMealDesc] = useState("");
  const [water, setWater] = useState("4");

  const close = () => {
    onClose && onClose();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (open === "mood") {
        await logMood({ mood, energy: energy[0], note: moodNote });
      } else if (open === "sleep") {
        await logSleep({ hours: parseFloat(sleepHours), quality: sleepQuality[0], note: sleepNote });
      } else if (open === "period") {
        await logCycle({ start_date: format(periodStart, "yyyy-MM-dd"), flow: periodFlow, notes: periodNote, symptoms: periodSymptoms });
      } else if (open === "fitness") {
        await logFitness({ activity, duration_minutes: parseInt(duration, 10), intensity });
      } else if (open === "skin") {
        await logSkin({ condition: skinCondition, hydration: skinHydration[0] });
      } else if (open === "nutrition") {
        await logNutrition({ meal_type: mealType, description: mealDesc || mealType, water_glasses: parseInt(water, 10) });
      }
      toast.success("Logged with care.");
      onLogged && onLogged();
      close();
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const titleMap = {
    mood: { title: "How are you feeling?", desc: "A quick check-in." },
    sleep: { title: "Last night's sleep", desc: "Hours and how it felt." },
    period: { title: "Log your period", desc: "Even an estimate helps." },
    fitness: { title: "Movement today", desc: "What did your body enjoy?" },
    skin: { title: "Skin check", desc: "How is your skin today?" },
    nutrition: { title: "Add a meal", desc: "A simple note works." },
  };
  const meta = titleMap[open] || { title: "", desc: "" };

  return (
    <Sheet open={!!open} onOpenChange={(v) => !v && close()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-w-[480px] mx-auto bg-[hsl(var(--card))]" data-testid={`log-sheet-${open}`}>
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl">{meta.title}</SheetTitle>
          <SheetDescription>{meta.desc}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
          {open === "mood" && (
            <>
              <div className="grid grid-cols-5 gap-2">
                {moodOptions.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMood(m.value)}
                    className={`flex flex-col items-center gap-1 py-3 rounded-2xl border text-sm transition ${
                      mood === m.value
                        ? "bg-[hsl(var(--secondary))] border-[hsl(var(--primary))]"
                        : "bg-[hsl(var(--card))] border-[hsl(var(--border))]"
                    }`}
                    data-testid={`log-mood-${m.value}`}
                  >
                    <span className="text-lg">{m.emoji}</span>
                    <span className="text-xs capitalize">{m.label}</span>
                  </button>
                ))}
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Energy: {energy[0]}/5</p>
                <Slider min={1} max={5} step={1} value={energy} onValueChange={setEnergy} data-testid="log-mood-energy-slider" />
              </div>
              <Textarea placeholder="A note (optional)" value={moodNote} onChange={(e) => setMoodNote(e.target.value)} data-testid="log-mood-note" />
            </>
          )}

          {open === "sleep" && (
            <>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Hours of sleep</p>
                <Input type="number" step="0.5" min="0" max="14" value={sleepHours} onChange={(e) => setSleepHours(e.target.value)} data-testid="log-sleep-hours" />
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Quality: {sleepQuality[0]}/5</p>
                <Slider min={1} max={5} step={1} value={sleepQuality} onValueChange={setSleepQuality} data-testid="log-sleep-quality-slider" />
              </div>
              <Textarea placeholder="How did the night feel?" value={sleepNote} onChange={(e) => setSleepNote(e.target.value)} data-testid="log-sleep-note" />
            </>
          )}

          {open === "period" && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="secondary" className="w-full justify-start" data-testid="log-period-date-button">
                    <CalendarDays size={16} className="mr-2" />
                    {format(periodStart, "MMMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                  <Calendar mode="single" selected={periodStart} onSelect={(d) => d && setPeriodStart(d)} disabled={(d) => d > new Date()} />
                </PopoverContent>
              </Popover>
              <Select value={periodFlow} onValueChange={setPeriodFlow}>
                <SelectTrigger data-testid="log-period-flow-select"><SelectValue placeholder="Flow intensity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="spotting">Spotting</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="heavy">Heavy</SelectItem>
                  <SelectItem value="very_heavy">Very Heavy</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--muted-foreground))] mb-2">Symptoms (optional)</p>
                <div className="flex flex-wrap gap-1.5">
                  {PERIOD_SYMPTOMS.map((s) => {
                    const active = periodSymptoms.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setPeriodSymptoms(
                          active ? periodSymptoms.filter((x) => x !== s) : [...periodSymptoms, s]
                        )}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition ${
                          active
                            ? "bg-[hsl(var(--plum))] text-[hsl(var(--ivory))] border-[hsl(var(--plum))]"
                            : "bg-[hsl(var(--ivory))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--plum))]"
                        }`}
                        data-testid={`log-period-symptom-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Textarea placeholder="Symptoms or notes" value={periodNote} onChange={(e) => setPeriodNote(e.target.value)} data-testid="log-period-note" />
            </>
          )}

          {open === "fitness" && (
            <>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger data-testid="log-fitness-activity-select"><SelectValue placeholder="Activity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk">Walk</SelectItem>
                  <SelectItem value="run">Run</SelectItem>
                  <SelectItem value="yoga">Yoga</SelectItem>
                  <SelectItem value="pilates">Pilates</SelectItem>
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="cycling">Cycling</SelectItem>
                  <SelectItem value="swim">Swim</SelectItem>
                  <SelectItem value="dance">Dance</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Duration (minutes)</p>
                <Input type="number" min="1" max="360" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="log-fitness-duration" />
              </div>
              <Select value={intensity} onValueChange={setIntensity}>
                <SelectTrigger data-testid="log-fitness-intensity-select"><SelectValue placeholder="Intensity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Gentle</SelectItem>
                  <SelectItem value="medium">Moderate</SelectItem>
                  <SelectItem value="high">Intense</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          {open === "skin" && (
            <>
              <div className="grid grid-cols-5 gap-2">
                {["clear", "dry", "oily", "breakout", "sensitive"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSkinCondition(c)}
                    className={`py-3 rounded-2xl border text-xs capitalize transition ${skinCondition === c ? "bg-[hsl(var(--secondary))] border-[hsl(var(--primary))]" : "bg-[hsl(var(--card))] border-[hsl(var(--border))]"}`}
                    data-testid={`log-skin-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Hydration: {skinHydration[0]}/5</p>
                <Slider min={1} max={5} step={1} value={skinHydration} onValueChange={setSkinHydration} data-testid="log-skin-hydration-slider" />
              </div>
            </>
          )}

          {open === "nutrition" && (
            <>
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger data-testid="log-nutrition-meal-select"><SelectValue placeholder="Meal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="snack">Snack</SelectItem>
                </SelectContent>
              </Select>
              <Textarea placeholder="A short description (e.g., 'oats + berries')" value={mealDesc} onChange={(e) => setMealDesc(e.target.value)} data-testid="log-nutrition-desc" />
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Water (glasses today)</p>
                <Input type="number" min="0" max="20" value={water} onChange={(e) => setWater(e.target.value)} data-testid="log-nutrition-water" />
              </div>
            </>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={close} data-testid="log-sheet-cancel">Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={submitting} data-testid="log-sheet-submit">
            {submitting ? "Saving…" : "Save"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
